import React from 'react';
import Hero from '../components/Hero';
import ContentSection from '../components/ContentSection';
import VideoContentSection from '../components/VideoContentSection';
import ImageGrid from '../components/ImageGrid';
import Testimonials from '../components/Testimonials';
import Location from '../components/Location';
import Featuring from '../components/Featuring';
import OpenHours from '../components/OpenHours';
import LiveMusicPromo from '../components/LiveMusicPromo';

// Import interior images
import interior0 from '../assets/home/interior/0.JPG';
import interior1 from '../assets/home/interior/1.JPG';
import interior2 from '../assets/home/interior/2.JPG';
import interior3 from '../assets/home/interior/3.JPG';
import interior4 from '../assets/home/interior/4.JPG';
import interior5 from '../assets/home/interior/5.JPG';
import interior6 from '../assets/home/interior/6.JPG';
import interior7 from '../assets/home/interior/7.JPG';
import interior8 from '../assets/home/interior/8.JPG';

// Import content section assets
import classicBenedict from '../assets/home/Classic Benidict .JPG';
import brunchBurger from '../assets/home/Brunch burger .JPG';
import breakfastVideo from '../assets/home/Drakes Breakfast Video.mov';
import craftCocktails from '../assets/home/craft cocktails.jpg';
import salmon from '../assets/home/salmon.jpg';
import desserts from '../assets/home/Desserts.JPG';
import dinner from '../assets/home/dinner.JPG';

// Create a data URL for a grey rectangle SVG with text
const placeholderImage = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg width="800" height="800" xmlns="http://www.w3.org/2000/svg">
    <rect width="100%" height="100%" fill="#E5E5E5"/>
    <text x="50%" y="50%" font-family="Inter, sans-serif" font-size="24" fill="#666" text-anchor="middle" dominant-baseline="middle">
      image here
    </text>
  </svg>`
)}`;

const Home = () => {

  // Content section data
  const contentSections = [
    {
      title: "Classic Benedict",
      description: "The combination of the savory components and the rich hollandaise sauce makes Eggs Benedict a luxurious and satisfying meal, perfect for special occasions or leisurely breakfasts.",
      imageOnRight: true,
      image: classicBenedict,
    },
    {
      title: "Brunch Burger",
      description: "This burger begins with a perfectly grilled Wagyu beef patty, renowned for its rich marbling and intense flavor. The patty is placed on a lightly toasted brioche bun, which adds a touch of sweetness and a soft texture. On top of the Wagyu patty, a perfectly fried egg is added. The egg is cooked to perfection, with a runny yolk that promises to ooze deliciously when bitten into, creating a rich sauce that enhances the flavors of the burger. For added flavor and texture, the burger is topped with a slice of gruyere cheese, crisp bacon, and a fresh, peppery bite of arugula. The combination of the savory Wagyu, the richness of the egg, and the flavorful toppings creates a decadent brunch experience that is both satisfying and indulgent.",
      imageOnRight: false,
      image: brunchBurger,
    }
  ];

  // Video section
  const videoSection = {
    title: "A Breakfast Experience Like No Other",
    description: "Start your day right at Drake's, where every morning brings a fresh perspective and delightful flavors. Our carefully crafted breakfast menu combines classic favorites with innovative twists, served in an atmosphere that feels like home.",
    videoSrc: breakfastVideo,
    videoOnRight: true
  };

  // Additional content sections
  const additionalSections = [
    {
      title: "Craft Cocktails",
      description: "Our cocktails represent a blend of artistry, creativity, and a passion for flavor, making them a dynamic and enjoyable part of modern drinking culture. Whether enjoyed at the bar or on the couches we offer a delightful experience for those who appreciate the finer details of mixology.",
      imageOnRight: true,
      image: craftCocktails,
    },
    {
      title: "Dinner",
      description: "Indulge in a menu crafted to elevate your evening, featuring fresh, locally-sourced ingredients. From savory starters to signature entrees and handcrafted cocktails, Drake's offers a dining experience perfect for every occasion. Whether you're savoring a perfectly grilled steak or sharing small plates with friends, dinner at Drake's is always a delight.",
      imageOnRight: false,
      image: dinner,
    },
    {
      title: "House Made Desserts",
      description: "Indulge in our classic Tiramisu, a timeless Italian dessert that promises to transport you to the heart of Italy with every bite. Layers of delicate, coffee-soaked ladyfingers are lovingly folded into a velvety mascarpone cream, all made in-house for the freshest flavor. Enhanced with a hint of rich local \"Groovy Goose\" coffee and coffee liqueur, this dessert showcases the vibrant flavors of our region's finest brews. Topped with a dusting of fine cocoa powder, it strikes the perfect balance between sweet and slightly bitter. Served chilled and elegantly presented, our Tiramisu is the perfect way to conclude your meal!",
      imageOnRight: true,
      image: desserts,
    }
  ];

  // Image grid data
  const interiorGridData = {
    title: "Experience Our Space",
    description: "At Drakes we elevate comfort food with love and care in every dish. Our menu features locally sourced ingredients, allowing us to create seasonal favorites like Salmon Risotto. Each plate is thoughtfully crafted, showcasing vibrant colors and flavors that evoke nostalgia. Join us for a warm and inviting dining experience, where every meal celebrates the joy of comfort food.",
    images: [
      { src: interior0, alt: "Drake's Interior View 1" },
      { src: interior1, alt: "Drake's Interior View 2" },
      { src: interior2, alt: "Drake's Interior View 3" },
      { src: interior3, alt: "Drake's Interior View 4" },
      { src: interior4, alt: "Drake's Interior View 5" },
      { src: interior5, alt: "Drake's Interior View 6" },
      { src: interior6, alt: "Drake's Interior View 7" },
      { src: interior7, alt: "Drake's Interior View 8" },
      { src: interior8, alt: "Drake's Interior View 9" }
    ]
  };

  return (
    <>
      <Hero />
      <OpenHours />
      <LiveMusicPromo />
      <VideoContentSection
        title={videoSection.title}
        description={videoSection.description}
        videoSrc={videoSection.videoSrc}
        videoOnRight={videoSection.videoOnRight}
      />
      {contentSections.map((section, index) => (
        <ContentSection
          key={`first-${index}`}
          title={section.title}
          description={section.description}
          image={section.image}
          imageAlt={`${section.title} Image`}
          imageOnRight={section.imageOnRight}
        />
      ))}
      {additionalSections.map((section, index) => (
        <ContentSection
          key={`additional-${index}`}
          title={section.title}
          description={section.description}
          image={section.image}
          imageAlt={`${section.title} Image`}
          imageOnRight={section.imageOnRight}
        />
      ))}
      <ImageGrid
        title={interiorGridData.title}
        description={interiorGridData.description}
        images={interiorGridData.images}
      />
      <Testimonials />
      <Featuring />
      <Location />
    </>
  );
};

export default Home; 